import React, { useEffect, useState } from 'react';
import { Modal, Button, Radio, Spin, Empty, Typography, Space, Tag, message } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const { Text, Title } = Typography;

export function isSuperAdminUser(user) {
  if (!user) return false;
  const roles = Array.isArray(user.roles)
    ? user.roles.map((ur) => ur?.role?.name).filter(Boolean)
    : [];
  return roles.includes('SUPER_ADMIN') || user.role === 'SUPER_ADMIN';
}

const OrganizationSelectorModal = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const isSuperAdmin = isSuperAdminUser(user);
  const hasOrg = !!user?.organizationId;
  // Sem organização vinculada o modal é obrigatório (não pode fechar)
  const required = isSuperAdmin && !hasOrg;

  // Abre automaticamente quando superadmin não tem organização vinculada
  useEffect(() => {
    if (isAuthenticated && isSuperAdmin && !user?.organizationId) {
      setOpen(true);
    }
  }, [isAuthenticated, isSuperAdmin, user?.organizationId]);

  // Carrega a lista de organizações ao abrir
  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await apiService.organizations.getAllForSuperAdmin();
        if (!cancelled) {
          const orgs = Array.isArray(list) ? list : [];
          setOrganizations(orgs);
          setSelectedId(user?.organizationId || (orgs.length === 1 ? orgs[0].id : null));
        }
      } catch (error) {
        if (!cancelled) {
          message.error('Erro ao carregar organizações');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSuperAdmin]);

  const handleSelect = async () => {
    if (!selectedId) {
      message.warning('Selecione uma organização para continuar');
      return;
    }
    setSaving(true);
    try {
      const result = await apiService.organizations.selectOrganization(selectedId);
      if (result?.user) {
        updateUser({
          organizationId: result.user.organizationId,
          organization: result.user.organization,
        });
      }
      message.success(result?.message || 'Organização vinculada com sucesso');
      setOpen(false);
    } catch (error) {
      message.error(error?.message || 'Erro ao vincular organização');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <Modal
      open={open}
      title={
        <Space>
          <BankOutlined />
          <span>Selecionar Organização</span>
        </Space>
      }
      onCancel={required ? undefined : () => setOpen(false)}
      maskClosable={false}
      closable={!required}
      width={520}
      footer={
        <Button
          type="primary"
          block
          loading={saving}
          disabled={!selectedId}
          onClick={handleSelect}
        >
          {hasOrg ? 'Trocar organização' : 'Vincular organização e continuar'}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          Você está logado como <Text strong>{user?.name}</Text> (Super Admin).
          {!hasOrg && ' Selecione a organização que deseja administrar:'}
          {hasOrg && ' Selecione outra organização para trocar o contexto atual:'}
        </Text>

        {hasOrg && (
          <div>
            <Text type="secondary">Organização atual: </Text>
            <Tag color="blue">{user?.organization?.name || '—'}</Tag>
          </div>
        )}

        <Spin spinning={loading}>
          {organizations.length === 0 && !loading ? (
            <Empty description="Nenhuma organização disponível" />
          ) : (
            <Radio.Group
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {organizations.map((org) => (
                  <Radio
                    key={org.id}
                    value={org.id}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--ant-color-border, #d9d9d9)',
                      borderRadius: 8,
                    }}
                  >
                    <Space direction="vertical" size={0}>
                      <Space size={8}>
                        <Text strong>{org.name}</Text>
                        {!org.isActive && <Tag color="red">Inativa</Tag>}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {org._count?.users ?? 0} usuário(s) · {org._count?.branches ?? 0} filial(is)
                        {org.dominio ? ` · ${org.dominio}` : ''}
                      </Text>
                    </Space>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        </Spin>
      </Space>
    </Modal>
  );
};

export default OrganizationSelectorModal;
